import React, { FC } from 'react';
import { View } from 'react-native';
import { EventTimelineData, TimeCategoryValues } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { NcFileBatchEventType } from '@v2/pages/common/enums';
import { scale as initialScale } from '@components/frame';
import EventBarChart, { EventBarChartProps } from '@v2/pages/common/components/NCFileBatchEvents/components/EventBarChart';
import { EventStatusSubStatusExecModeProps } from '@v2/pages/common/components/NCFileBatchEvents/components/EventStatusSubStatusExecMode';
import { DEFAULT_EVENT_KEY_FIELDS } from '@v2/pages/common/components/NCFileBatchEvents/constants';

export interface BarChartCellProps
  extends Partial<TimeCategoryValues>,
    Partial<Pick<EventTimelineData, 'maxTimeInSec' | 'showDots'>>,
    Pick<EventBarChartProps, 'C' | 'eventType' | 'eventsPerspective' | 'scale'>,
    Pick<EventStatusSubStatusExecModeProps, 'event'> {
  firstElementKeyValue?: number | null;
}

const BarChartCell: FC<BarChartCellProps> = ({
  event,
  eventType,
  eventsPerspective,
  C,
  scale = initialScale,
  maxTimeInSec = 0,
  idealTimeInSec = 0,
  paidTimeInSec = 0,
  actualTimeInSec = 0,
  scheduledTimeInSec = 0,
  showDots = false,
  firstElementKeyValue,
}) => {
  // Compute isFirstElement by comparing event's key value with firstElementKeyValue
  const isFirstElement = (() => {
    if (firstElementKeyValue == null) return false;
    for (const field of DEFAULT_EVENT_KEY_FIELDS) {
      const value = (event as Record<string, unknown>)?.[field];
      if (value != null) return value === firstElementKeyValue;
    }
    return false;
  })();

  // Extract time values from event based on event type
  let activeTimeInSec = 0;
  let changeTimeInSec = 0;
  let setupTimeInSec = 0;

  switch (eventType) {
    case NcFileBatchEventType.CYCLE:
      activeTimeInSec = event?.durationActiveInSeconds || 0;
      changeTimeInSec = event?.durationChangeInSeconds || 0;
      break;
    case NcFileBatchEventType.ACTIVE:
      activeTimeInSec = event?.durationActiveInSeconds || 0;
      break;
    case NcFileBatchEventType.CHANGE:
      changeTimeInSec = event?.durationChangeInSeconds || 0;
      break;
    case NcFileBatchEventType.SETUP:
      setupTimeInSec = event?.actualSetupTimeInSeconds || 0;
      break;
    default:
      return null;
  }

  return (
    <View style={{ width: '100%' }}>
      <EventBarChart
        activeTimeInSec={activeTimeInSec}
        changeTimeInSec={changeTimeInSec}
        setupTimeInSec={setupTimeInSec}
        maxTimeInSec={maxTimeInSec}
        idealTimeInSec={idealTimeInSec}
        paidTimeInSec={paidTimeInSec}
        actualTimeInSec={actualTimeInSec}
        scheduledTimeInSec={scheduledTimeInSec}
        showDots={showDots}
        eventType={eventType}
        eventsPerspective={eventsPerspective}
        C={C}
        scale={scale}
        isFirstElement={isFirstElement}
      />
    </View>
  );
};

export default React.memo(BarChartCell, (prev, next) => {
  return (
    prev.event.sequenceNumber === next.event.sequenceNumber &&
    prev.eventType === next.eventType &&
    prev.C.scheme === next.C.scheme &&
    prev.maxTimeInSec === next.maxTimeInSec &&
    prev.idealTimeInSec === next.idealTimeInSec &&
    prev.paidTimeInSec === next.paidTimeInSec &&
    prev.scheduledTimeInSec === next.scheduledTimeInSec &&
    prev.showDots === next.showDots &&
    prev.firstElementKeyValue === next.firstElementKeyValue
  );
});
